// Pure reducer: context + inputs + rules -> trace & result
import { CalculatorConfig, CalculatorExecutionResult, RuleTraceStep } from '../types';
import { compileExpression } from './compiler';
import { getDefaultValueForInput } from '../useCalculatorRunnerState';

export function runPipeline(
  config: CalculatorConfig,
  inputValues: Record<string, any>,
  initialContext: Record<string, any> = {},
  customFunctions?: Record<string, (...args: any[]) => any>
): CalculatorExecutionResult {
  const currentValues: Record<string, any> = {
    ...initialContext,
  };

  // Populate default values from config inputs if not already set
  if (config.inputs) {
    for (const input of config.inputs) {
      if (input.name) {
        currentValues[input.name] = getDefaultValueForInput(input);
      }
    }
  }

  // Override with provided input values
  Object.assign(currentValues, inputValues);

  // Populate extraValues for select inputs (<inputName>_val1, <inputName>_val2, etc.)
  if (config.inputs) {
    for (const input of config.inputs) {
      if (input.type === 'select' && input.name && input.options) {
        const currentVal = currentValues[input.name];
        const matchedOption = input.options.find(
          (opt) => String(opt.value) === String(currentVal)
        );
        if (matchedOption && matchedOption.extraValues && Array.isArray(matchedOption.extraValues)) {
          matchedOption.extraValues.forEach((ev, idx) => {
            currentValues[`${input.name}_val${idx + 1}`] = ev;
          });
        }
      }
    }
  }

  const trace: RuleTraceStep[] = [];
  const errors: string[] = [];
  const missingFieldsSet = new Set<string>();
  const fieldErrors: Record<string, string> = {};

  // Custom prop accessor to detect empty/missing inputs during expression evaluation
  const customProp = (name: string, _get: (n: string) => any, obj: any) => {
    const val = obj ? obj[name] : undefined;
    if (val === '' || val === undefined || val === null || (typeof val === 'number' && Number.isNaN(val))) {
      const err: any = new Error(`Field "${name}" is empty`);
      err.isMissingField = true;
      err.missingField = name;
      throw err;
    }
    return val;
  };

  const compilerOptions = { customProp };

  for (const rule of config.rules || []) {
    if (rule.enabled === false) {
      continue;
    }

    const valueBefore = currentValues[rule.targetField];
    let conditionMatched = true;

    // Evaluate condition expression if specified
    if (rule.condition && rule.condition.trim()) {
      try {
        const conditionFn = compileExpression(rule.condition, customFunctions, compilerOptions);
        const conditionResult = conditionFn(currentValues);
        if (conditionResult instanceof Error) {
          const err: any = conditionResult;
          if (err.isMissingField || err.missingField) {
            const field = err.missingField;
            missingFieldsSet.add(field);
            fieldErrors[field] = 'Заполните поле для выполнения расчета';
            trace.push({
              ruleId: rule.id,
              ruleName: rule.name,
              conditionMatched: false,
              formula: rule.formula,
              valueBefore,
              valueAfter: undefined,
              missingField: field,
            });
            // Mark target field as undefined/ellipsis state
            currentValues[rule.targetField] = undefined;
            continue;
          }
          throw err;
        }
        conditionMatched = Boolean(conditionResult);
      } catch (err: any) {
        if (err.isMissingField || err.missingField) {
          const field = err.missingField;
          missingFieldsSet.add(field);
          fieldErrors[field] = 'Заполните поле для выполнения расчета';
          trace.push({
            ruleId: rule.id,
            ruleName: rule.name,
            conditionMatched: false,
            formula: rule.formula,
            valueBefore,
            valueAfter: undefined,
            missingField: field,
          });
          currentValues[rule.targetField] = undefined;
          continue;
        }
        const errMsg = `Condition evaluation error in rule "${rule.name}": ${err?.message || err}`;
        errors.push(errMsg);
        trace.push({
          ruleId: rule.id,
          ruleName: rule.name,
          conditionMatched: false,
          formula: rule.formula,
          valueBefore,
          valueAfter: valueBefore,
          error: errMsg,
        });
        continue;
      }
    }

    if (!conditionMatched) {
      trace.push({
        ruleId: rule.id,
        ruleName: rule.name,
        conditionMatched: false,
        formula: rule.formula,
        valueBefore,
        valueAfter: valueBefore,
      });
      continue;
    }

    // Evaluate formula expression
    try {
      const formulaFn = compileExpression(rule.formula, customFunctions, compilerOptions);
      const valueAfter = formulaFn(currentValues);

      if (valueAfter instanceof Error) {
        const err: any = valueAfter;
        if (err.isMissingField || err.missingField) {
          const field = err.missingField;
          missingFieldsSet.add(field);
          fieldErrors[field] = 'Заполните поле для выполнения расчета';
          currentValues[rule.targetField] = undefined;
          trace.push({
            ruleId: rule.id,
            ruleName: rule.name,
            conditionMatched: true,
            formula: rule.formula,
            valueBefore,
            valueAfter: undefined,
            missingField: field,
          });
          continue;
        }
        throw err;
      }

      let finalValue = valueAfter;
      if (
        rule.precision !== undefined &&
        rule.precision !== null &&
        (rule.precision as any) !== '' &&
        typeof valueAfter === 'number' &&
        !Number.isNaN(valueAfter)
      ) {
        const prec = Number(rule.precision);
        if (!Number.isNaN(prec)) {
          finalValue = Number(valueAfter.toFixed(prec));
        }
      }

      currentValues[rule.targetField] = finalValue;

      trace.push({
        ruleId: rule.id,
        ruleName: rule.name,
        conditionMatched: true,
        formula: rule.formula,
        valueBefore,
        valueAfter: finalValue,
      });
    } catch (err: any) {
      if (err.isMissingField || err.missingField) {
        const field = err.missingField;
        missingFieldsSet.add(field);
        fieldErrors[field] = 'Заполните поле для выполнения расчета';
        currentValues[rule.targetField] = undefined;
        trace.push({
          ruleId: rule.id,
          ruleName: rule.name,
          conditionMatched: true,
          formula: rule.formula,
          valueBefore,
          valueAfter: undefined,
          missingField: field,
        });
        continue;
      }

      const errMsg = `Formula execution error in rule "${rule.name}": ${err?.message || err}`;
      errors.push(errMsg);
      trace.push({
        ruleId: rule.id,
        ruleName: rule.name,
        conditionMatched: true,
        formula: rule.formula,
        valueBefore,
        valueAfter: valueBefore,
        error: errMsg,
      });
    }
  }

  return {
    values: currentValues,
    trace,
    errors,
    missingFields: Array.from(missingFieldsSet),
    fieldErrors,
  };
}

